import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  MenuItem,
  OutlinedInput,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Tooltip,
} from "@mui/material";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  DisabledJobRatings,
  JobOrder,
  jobOrderSearchParams,
  JobRating,
  JobRatingLabel,
} from "../common/JobOrder";
import { useGetJobOrdersQuery } from "../redux/api/jobOrder";
import { useGetPortsQuery, useGetVesselsQuery } from "../redux/api/vessel";
import { useGetUsersByGroupQuery } from "../redux/api/user";
import "./JobOrderListComponent.scss";
import { jobRatingStringToEnum } from "../util/jobRating.util";
import { Vessel } from "../common/Vessel";
import { User, UserGroup } from "../common/User";
import { colorFromName } from "../common/ColorFromName";
import moment from "moment";
import useDocumentTitle from "../util/useDocumentTitle";
import { formatDateString } from "../common/FormatDateString";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../redux/authentication/authSlice";
import { DisabledPorts, PortMasterLabel } from "../common/Port";
import { useGetDispatchesQuery } from "../redux/api/dispatch";
import { DispatchSearchParams } from "../common/Dispatch";
import { MarinerProfile } from "../common/MarinerProfile";
import MarinerProfileAutocomplete from "./marinerProfile/MarinerProfileAutocomplete";

type job = {
  id: number;
  date: number;
  vessel: string;
  assignee: User;
  rating: string;
  port: string;
  offsigner: string;
  offsignerId: number | undefined;
  onsigner: string;
  completed: number;
  notApplicable: number;
  tasks: number;
  critical: boolean;
  newJobOrder: boolean;
  modifiedByCaptain: boolean;
};

const JobOrderListComponent = () => {
  useDocumentTitle("Job Orders");
  const navigate = useNavigate();
  const redirectToJobOrder = () => {
    const newJobOrderId = "new";
    navigate(`/jobOrders/${newJobOrderId}`);
  };
  const user = useSelector(selectCurrentUser);
  const isViewOnly =
    (user?.userGroup as UserGroup) === UserGroup.CREWING_EXECS ||
    (user?.userGroup as UserGroup) === UserGroup.HUMAN_RESOURCES ||
    (user?.userGroup as UserGroup) === UserGroup.FLEET_EXECS ||
    (user?.userGroup as UserGroup) === UserGroup.VESSEL_EXECS;
  const isExecutive =
    (user?.userGroup as UserGroup) === UserGroup.CREWING_EXECS ||
    (user?.userGroup as UserGroup) === UserGroup.FLEET_EXECS ||
    (user?.userGroup as UserGroup) === UserGroup.VESSEL_EXECS;
  const [jobOrderSearchParams, setJobOrderSearchParams] =
    useState<jobOrderSearchParams>({
      assigneeId: "",
      vesselId: "",
      port: "",
      rating: undefined,
      compatibleWithRating: undefined,
      jobOrderID: "",
      fillByAfterDate: undefined,
      fillByBeforeDate: undefined,
      completed: false,
      notApplicable: false,
      completedOrNotApplicable: undefined,
      deleted: false,
      archived: false,
      critical: undefined,
      newJobOrder: undefined,
      modifiedByCaptain: undefined,
      publishStatuses: [],
      smis: undefined,
      profileId: undefined,
      page: 0,
      size: 15,
      sortColumn: "fillByDate",
      sortDirection: "asc",
    });
  const [dispatchSearchParams, setDispatchSearchParams] =
    useState<DispatchSearchParams>({
      vesselId: undefined,
      fleetId: undefined,
      marinerIds: [0], // insert a temporary id, as it will search for all mariners if an empty array is provided
      ratings: undefined,
      current: true,
    });
  const [searchParams] = useSearchParams();
  const [ports, setPorts] = useState<string[]>([]);
  const [vessel, setVessel] = useState<Vessel | null>(null);
  const [port, setPort] = useState<string | null>(null);
  const [assignee, setAssignee] = useState<User | null>(null);
  const [rating, setRating] = useState<JobRating | null>(null);
  const [status, setStatus] = useState<string[]>([]);
  const [smis, setSmis] = useState<string>("");
  const [profile, setProfile] = useState<MarinerProfile | null>(null);
  // variable to skip the initial GET query until the query params have been processed
  const [skipQuery, setSkipQuery] = useState(
    Array.from(searchParams).length > 0
  );
  // variable to track whether the query params have been processed
  const [searchParamsProcessed, setSearchParamsProcessed] = useState(false);
  type TabValue =
    | "activeJobOrders"
    | "pendingJobOrders"
    | "completedJobOrders"
    | "deletedJobOrders"
    | "allJobOrders";
  const [currentTab, setCurrentTab] = useState<TabValue>("activeJobOrders");

  const { data: vessels, isLoading: isVesselLoading } = useGetVesselsQuery({});
  const { data: specialists, isLoading: isSpecialistsLoading } =
    useGetUsersByGroupQuery(UserGroup.CREWING_SPECIALISTS);
  const { data: managers, isLoading: isManagersLoading } =
    useGetUsersByGroupQuery(UserGroup.CREWING_MANAGERS);
  const { data: evalSpecialists, isLoading: isEvalSpecialistsLoading } =
    useGetUsersByGroupQuery(UserGroup.CREWING_EVAL_SPECIALISTS);
  const assignees = useMemo(() => {
    return [...(specialists || []), ...(managers || []), ...(evalSpecialists || [])];
  }, [specialists, managers, evalSpecialists]);

  const {
    data: jobOrdersFromApi,
    isLoading: isJobOrdersLoading,
    refetch,
  } = useGetJobOrdersQuery(jobOrderSearchParams, {
    skip: skipQuery,
    refetchOnMountOrArgChange: true,
  });
  const { data: dispatchesFromApi, isLoading: isDispatchesLoading } =
    useGetDispatchesQuery(dispatchSearchParams, {
      skip: skipQuery,
      refetchOnMountOrArgChange: true,
    });
  const { data: portsFromApi } = useGetPortsQuery();
  let jobOrders: job[] = (jobOrdersFromApi?.content as JobOrder[])?.map(
    (job: any) => {
      return {
        id: job.id,
        date: job.fillByDate,
        vessel: job.vessel?.name,
        assignee: job.assignee,
        rating: job.rating,
        port: job.port,
        offsigner: !!job.offsigner
          ? job.offsigner?.firstName + " " + job.offsigner?.lastName
          : "",
        offsignerId: job.offsigner?.id,
        onsigner: !!job.onsigner
          ? job.onsigner?.firstName + " " + job.onsigner?.lastName
          : "",
        completed: job.checklistRoot?.subTasksCompletedCount | 0,
        notApplicable: job.checklistRoot?.subTasksNotApplicableCount | 0,
        tasks: job.checklistRoot?.subTasksCount | 0,
        critical:
          moment(new Date(job.fillByDate).toISOString()).diff(
            moment(new Date()),
            "days"
          ) < 7
            ? true
            : false,
        newJobOrder: job.unopenedJobOrder,
        modifiedByCaptain: job.modifiedByCaptain
      };
    }
  );

  useEffect(() => {
    (async () => {
      searchParams.forEach((s, k) => {
        if (!s) return;
        switch (k) {
          case "assigneeId":
            if (!s) break;
            const currAssignee = assignees.find((a) => a.id === s);
            setAssignee(currAssignee ? currAssignee : null);
            break;
          case "vesselId":
            if (!Number(s)) break;
            const currVessel = vessels?.find((v) => v.id === Number(s));
            setVessel(currVessel ? currVessel : null);
            break;
          // Doesn't support PORT at the moment, because of complications between this and updating the Port list on Vessel change
          // case "port":
          //   temp = { ...temp, port: s };
          //   break;
          case "rating":
            const currRating = jobRatingStringToEnum(s);
            setRating(currRating ? currRating : null);
            break;
          default:
            // do nothing
            break;
        }
      });
      setSearchParamsProcessed(true);
    })();
  }, [assignees, searchParams, vessels]);

  useEffect(() => {
    // if skipQuery is already disabled, do nothing
    if (skipQuery && searchParamsProcessed) {
      setSkipQuery(false);
    }
    // no need to add skipQuery to the dependency, since we are setting it here and the if() block is just to save a call
    // we don't want to add searchParamsProcessed to the dependencies, since it would call it too early
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobOrderSearchParams]);

  useEffect(() => {
    let portOptions;
    if (!!vessel?.ports) {
      portOptions = vessel.ports;
    } else {
      portOptions = portsFromApi;
      //filter out disabled ports
      portOptions = portOptions?.filter(
        (port) => !DisabledPorts.some((dis) => PortMasterLabel[dis] === port)
      );
    }
    if (!!portOptions) {
      setPorts(["TBD", ...portOptions]);
    }
  }, [vessel?.ports, portsFromApi]);

  // use effects to update the JobOrderSearchParams whenever the relevant state variables are changes
  // Disable the dependency rule because this leads to an infinite loop.
  // In addition, the expected logical flow is that the
  // User => interacts with vessel/assignee/rating state => updates jobOrderSearchParams (One-Way)
  useEffect(() => {
    setJobOrderSearchParams({
      ...jobOrderSearchParams,
      vesselId: vessel?.id ? `${vessel.id}` : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vessel]);
  useEffect(() => {
    setJobOrderSearchParams({
      ...jobOrderSearchParams,
      assigneeId: assignee?.id ? `${assignee.id}` : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignee]);
  useEffect(() => {
    setJobOrderSearchParams({
      ...jobOrderSearchParams,
      rating: rating ? rating : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rating]);
  useEffect(() => {
    setJobOrderSearchParams({
      ...jobOrderSearchParams,
      port: port ? port : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [port]);
  useEffect(() => {
    setJobOrderSearchParams({
      ...jobOrderSearchParams,
      critical: status.includes("critical") ? true : undefined,
      newJobOrder: status.includes("new") ? true : undefined,
      modifiedByCaptain: status.includes("modified") ? true : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);
  useEffect(() => {
    setJobOrderSearchParams({
      ...jobOrderSearchParams,
      smis: !!smis ? smis : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smis]);
  useEffect(() => {
    setJobOrderSearchParams({
      ...jobOrderSearchParams,
      profileId: !!profile?.id ? `${profile.id}` : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);
  useEffect(() => {
    if (isViewOnly || !user?.username) {
      return;
    }
    setAssignee(assignees.find((a) => a.id === user.username) || null);
  }, [assignees, isViewOnly, user]);
  // useEffect to update DispatchSearchParams to only search for relevant offsigner ids based on retrieved Job Orders
  useEffect(() => {
    if (!!jobOrdersFromApi) {
      setDispatchSearchParams({
        ...dispatchSearchParams,
        // @ts-ignore ignore this type check since typescript cannot read that undefined is impossible from the filter() function
        marinerIds: jobOrdersFromApi.content
          .filter((jo) => !!jo.offsigner?.id)
          // @ts-ignore ignore this type check since typescript cannot read that undefined is impossible from the filter() function
          .map((jo) => jo.offsigner.id),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobOrdersFromApi]);

  const handleSort =
    (property: keyof JobOrder) => (event: React.MouseEvent<unknown>) => {
      const direction =
        jobOrderSearchParams.sortColumn === property &&
        jobOrderSearchParams.sortDirection === "asc"
          ? "desc"
          : "asc";
      setJobOrderSearchParams({
        ...jobOrderSearchParams,
        sortColumn: property,
        sortDirection: direction,
      });

      refetch();
    };

  const handleActiveTabToggle = (newTab: TabValue) => {
    switch (newTab) {
      case "activeJobOrders": {
        let currUserId = "";
        if (!isViewOnly && !!user?.username) {
          const currAssignee =
            assignees.find((a) => a.id === user.username) || null;
          if (!!currAssignee) {
            currUserId = user.username;
          }
          setAssignee(currAssignee);
        }
        setJobOrderSearchParams({
          assigneeId: !isViewOnly ? currUserId : "",
          vesselId: "",
          port: "",
          rating: undefined,
          compatibleWithRating: undefined,
          jobOrderID: "",
          fillByAfterDate: undefined,
          fillByBeforeDate: undefined,
          completed: false,
          notApplicable: false,
          completedOrNotApplicable: undefined,
          deleted: false,
          archived: false,
          critical: undefined,
          newJobOrder: undefined,
          modifiedByCaptain: undefined,
          publishStatuses: [],
          smis: undefined,
          profileId: undefined,
          page: 0,
          size: 15,
          sortColumn: "fillByDate",
          sortDirection: "asc",
        });
        break;
      }
      case "pendingJobOrders": {
        setJobOrderSearchParams({
          assigneeId: "",
          vesselId: "",
          port: "",
          rating: undefined,
          compatibleWithRating: undefined,
          jobOrderID: "",
          fillByAfterDate: undefined,
          fillByBeforeDate: undefined,
          completed: undefined,
          notApplicable: undefined,
          completedOrNotApplicable: true,
          deleted: false,
          archived: false,
          critical: undefined,
          newJobOrder: undefined,
          modifiedByCaptain: undefined,
          publishStatuses: [],
          smis: undefined,
          profileId: undefined,
          page: 0,
          size: 15,
          sortColumn: "fillByDate",
          sortDirection: "asc",
        });
        break;
      }
      case "completedJobOrders": {
        setJobOrderSearchParams({
          assigneeId: "",
          vesselId: "",
          port: "",
          rating: undefined,
          compatibleWithRating: undefined,
          jobOrderID: "",
          fillByAfterDate: undefined,
          fillByBeforeDate: undefined,
          completed: undefined,
          notApplicable: undefined,
          completedOrNotApplicable: undefined,
          critical: undefined,
          newJobOrder: undefined,
          modifiedByCaptain: undefined,
          publishStatuses: [],
          smis: undefined,
          profileId: undefined,
          deleted: undefined,
          archived: true,
          page: 0,
          size: 15,
          sortColumn: "fillByDate",
          sortDirection: "asc",
        });
        break;
      }
      case "deletedJobOrders": {
        setJobOrderSearchParams({
          assigneeId: "",
          vesselId: "",
          port: "",
          rating: undefined,
          compatibleWithRating: undefined,
          jobOrderID: "",
          fillByAfterDate: undefined,
          fillByBeforeDate: undefined,
          completed: undefined,
          notApplicable: undefined,
          completedOrNotApplicable: undefined,
          critical: undefined,
          newJobOrder: undefined,
          modifiedByCaptain: undefined,
          publishStatuses: [],
          smis: undefined,
          profileId: undefined,
          deleted: true,
          archived: false,
          page: 0,
          size: 15,
          sortColumn: "fillByDate",
          sortDirection: "asc",
        });
        break;
      }
      case "allJobOrders": {
        setJobOrderSearchParams({
          assigneeId: "",
          vesselId: "",
          port: "",
          rating: undefined,
          compatibleWithRating: undefined,
          jobOrderID: "",
          fillByAfterDate: undefined,
          fillByBeforeDate: undefined,
          completed: undefined,
          notApplicable: undefined,
          completedOrNotApplicable: undefined,
          critical: undefined,
          newJobOrder: undefined,
          modifiedByCaptain: undefined,
          publishStatuses: [],
          smis: undefined,
          profileId: undefined,
          deleted: undefined,
          archived: undefined,
          page: 0,
          size: 15,
          sortColumn: "fillByDate",
          sortDirection: "asc",
        });
        break;
      }
    }

    if (newTab !== "activeJobOrders") {
      setAssignee(null);
    }
    setStatus([]);
    setVessel(null);
    setPort(null);
    setRating(null);
    setSmis("");
    setProfile(null);

    setCurrentTab(newTab);
  };

  if (
    isSpecialistsLoading ||
    isManagersLoading ||
    isEvalSpecialistsLoading ||
    isJobOrdersLoading ||
    isDispatchesLoading ||
    isVesselLoading
  ) {
    return (
      <Box sx={{ display: "flex" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <div className="page-header tab-header">
        <div className="flex align-items-center g-3">
          <h1>Job Orders</h1>
          {!isViewOnly && (
            <Button
              variant="contained"
              className="blue-button h-4"
              onClick={redirectToJobOrder}
            >
              New Job Order
            </Button>
          )}
        </div>
        <Tabs
          value={currentTab}
          onChange={(e, newTab) => handleActiveTabToggle(newTab)}
          aria-label="Job Order Listing Page Tabs"
          className="top-right-tabs"
        >
          <Tab value="activeJobOrders" label="Active" />
          <Tab value="pendingJobOrders" label="Pending" />
          <Tab value="completedJobOrders" label="Completed" />
          <Tab value="deletedJobOrders" label="Deleted" />
          <Tab value="allJobOrders" label="All" />
        </Tabs>
      </div>
      <div className="filters-wrap">
        <div className="filters-list job-orders-filters">
          <label className="large-filter" style={{ width: "180px" }}>
            <span>Assignee:</span>
            <Autocomplete
              id="Assignee"
              value={assignee}
              options={assignees}
              onChange={(event, value) => setAssignee(value)}
              renderInput={(params) => <TextField {...params} />}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              getOptionLabel={(opt) => opt.name || opt.email}
            />
          </label>
          {!!currentTab && currentTab === "activeJobOrders" && (
            <label className="small-filter">
              <span>Status:</span>
              <Select
                id="status"
                value={status}
                onChange={(e) => {
                  const {
                    target: { value },
                  } = e;
                  const status =
                    typeof value === "string" ? value.split(",") : value;
                  if (status.includes("")) {
                    setStatus([]);
                  } else {
                    setStatus(status);
                  }
                }}
                multiple
              >
                <MenuItem value={"critical"}>Critical</MenuItem>
                <MenuItem value={"new"}>New Job Orders</MenuItem>
                <MenuItem value={"modified"}>Modified By Captain</MenuItem>
                <MenuItem value={""}>Show All</MenuItem>
              </Select>
            </label>
          )}
          <label style={{ width: "180px" }}>
            <span>SMIS #</span>
            <TextField
              id="smis"
              onChange={(e) => setSmis(e.target.value)}
              value={smis}
              variant="outlined"
              placeholder="SMIS #"
              size="small"
            />
          </label>
          <label className="large-filter" style={{ width: "240px" }}>
            <span>Mariner</span>
            <MarinerProfileAutocomplete
              id="onsignerAutocomplete"
              value={profile}
              marinerRating={undefined}
              onChange={(p) =>
                p === undefined ? setProfile(null) : setProfile(p)
              }
            />
          </label>
          <label className="large-filter" style={{ width: "200px" }}>
            <span>Vessel:</span>
            <Autocomplete
              options={
                vessels ? (vessels as Vessel[]).filter((v) => !v.disabled) : []
              }
              value={vessel}
              onChange={(event, value) => setVessel(value)}
              renderInput={(params) => <TextField {...params} />}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              getOptionLabel={(opt) => (opt.name ? opt.name : "")}
            />
          </label>
          <label>
            <span>Port Relief Requested:</span>
            <Autocomplete
              id="port"
              options={ports}
              value={port}
              onChange={(event, value) => setPort(value)}
              renderInput={(params) => <TextField {...params} />}
              getOptionLabel={(opt) => (opt ? opt : "")}
            />
          </label>
          <label>
            <span>Rating:</span>
            <Autocomplete
              id="Rating"
              options={Object.keys(JobRating).filter(
                (rating) =>
                  !DisabledJobRatings.has(JobRating[rating as JobRating])
              )}
              getOptionLabel={(option) => JobRatingLabel[option as JobRating]}
              value={rating}
              onChange={(event, value: any) => {
                setRating(JobRating[value as JobRating]);
              }}
              renderInput={(params) => <TextField {...params} />}
            />
          </label>
          <label className="small-filter">
            <span>Job Order ID:</span>
            <OutlinedInput
              value={jobOrderSearchParams.jobOrderID || ""}
              onChange={(e) =>
                setJobOrderSearchParams({
                  ...jobOrderSearchParams,
                  jobOrderID: e.target.value,
                })
              }
              type="text"
            />
          </label>
        </div>
      </div>
      <TableContainer className="job-order-table">
        <Table className="table">
          <TableHead>
            <TableRow>
              <TableCell align="center" className="rating">
                <>Assignee</>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  className="flex align-items-center"
                  active={jobOrderSearchParams.sortColumn === "id"}
                  direction={
                    jobOrderSearchParams.sortColumn === "id"
                      ? jobOrderSearchParams.sortDirection
                      : "asc"
                  }
                  onClick={handleSort("id")}
                >
                  Job Order ID
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={jobOrderSearchParams.sortColumn === "fillByDate"}
                  direction={
                    jobOrderSearchParams.sortColumn === "fillByDate"
                      ? jobOrderSearchParams.sortDirection
                      : "asc"
                  }
                  onClick={handleSort("fillByDate")}
                >
                  Crew Change Needed
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={jobOrderSearchParams.sortColumn === "vessel"}
                  direction={
                    jobOrderSearchParams.sortColumn === "vessel"
                      ? jobOrderSearchParams.sortDirection
                      : "asc"
                  }
                  onClick={handleSort("vessel")}
                >
                  Vessel
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={jobOrderSearchParams.sortColumn === "port"}
                  direction={
                    jobOrderSearchParams.sortColumn === "port"
                      ? jobOrderSearchParams.sortDirection
                      : "asc"
                  }
                  onClick={handleSort("port")}
                >
                  Port Relief Requested
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={jobOrderSearchParams.sortColumn === "rating"}
                  direction={
                    jobOrderSearchParams.sortColumn === "rating"
                      ? jobOrderSearchParams.sortDirection
                      : "asc"
                  }
                  onClick={handleSort("rating")}
                >
                  Rating
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={jobOrderSearchParams.sortColumn === "offsigner"}
                  direction={
                    jobOrderSearchParams.sortColumn === "offsigner"
                      ? jobOrderSearchParams.sortDirection
                      : "asc"
                  }
                  onClick={handleSort("offsigner")}
                >
                  Offsigner
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <>Days Onboard at Signoff</>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={jobOrderSearchParams.sortColumn === "onsigner"}
                  direction={
                    jobOrderSearchParams.sortColumn === "onsigner"
                      ? jobOrderSearchParams.sortDirection
                      : "asc"
                  }
                  onClick={handleSort("onsigner")}
                >
                  Onsigner
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={jobOrderSearchParams.sortColumn === "checklistRoot"}
                  direction={
                    jobOrderSearchParams.sortColumn === "checklistRoot"
                      ? jobOrderSearchParams.sortDirection
                      : "asc"
                  }
                  onClick={handleSort("checklistRoot")}
                >
                  Progress
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobOrders?.map((row, index) => {
              return (
                <TableRow key={"jobOrder" + index}>
                  <TableCell className="centered rating">
                    <Tooltip
                      title={row.assignee?.name || row.assignee?.email || "Deleted user"}
                      placement="right"
                    >
                      <Avatar
                        sx={{
                          bgcolor: colorFromName(row.assignee?.name || ""),
                          height: 30,
                          width: 30,
                        }}
                      >
                        {!!row.assignee?.name
                          ? row.assignee.name.substring(0, 1)
                          : undefined}
                      </Avatar>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {!isExecutive ? (
                      <Link className="blue-link" to={`/jobOrders/${row.id}`}>
                        {row.id}
                      </Link>
                    ) : (
                      <p>{row.id}</p>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flag-cell">
                      <div>{formatDateString(row.date.toString())} </div>
                      <div className="flex g-1">
                        {!!currentTab &&
                          currentTab === "activeJobOrders" &&
                          !!row.critical && (
                            <span className="critical-flag">Critical</span>
                          )}

                        {!!currentTab &&
                          currentTab === "activeJobOrders" &&
                          !!row.newJobOrder && (
                            <span className="critical-flag bg-navy">New</span>
                          )}
                        
                        {!!currentTab &&
                          currentTab === "activeJobOrders" &&
                          !!row.modifiedByCaptain && (
                            <span className="critical-flag bg-grey">M</span>
                          )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{row.vessel}</TableCell>
                  <TableCell className="wd-auto">{row.port}</TableCell>
                  <TableCell className=" wb-wrap">
                    <Tooltip
                      title={JobRatingLabel[row.rating as JobRating]}
                      placement="right"
                    >
                      <span>{row.rating.substring(1)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{row.offsigner}</TableCell>
                  <TableCell>
                    <div className="flex justify-content-center">
                      {!!row.offsignerId &&
                        !!dispatchesFromApi?.find(
                          (d) =>
                            d.profileId === row.offsignerId && !!d.startDate
                        ) &&
                        !!row.date && (
                          <span>
                            {moment(row.date)
                              .startOf("day")
                              .isSameOrAfter(
                                moment(
                                  // use filter().slice(-1)[0] to get the last element that matches, as opposed to .find()
                                  // current typescript version has issues with .findLast()
                                  // @ts-ignore ignore this type check since typescript cannot read that undefined is impossible the array functions above
                                  dispatchesFromApi
                                    .filter(
                                      (d) =>
                                        d.profileId === row.offsignerId &&
                                        !!d.startDate
                                    )
                                    .slice(-1)[0].startDate
                                ).startOf("day")
                              )
                              ? Math.ceil(
                                  moment(row.date)
                                    .startOf("day")
                                    .diff(
                                      moment(
                                        // use filter().slice(-1)[0] to get the last element that matches, as opposed to .find()
                                        // current typescript version has issues with .findLast()
                                        // @ts-ignore ignore this type check since typescript cannot read that undefined is impossible the array functions above
                                        dispatchesFromApi
                                          .filter(
                                            (d) =>
                                              d.profileId === row.offsignerId &&
                                              !!d.startDate
                                          )
                                          .slice(-1)[0].startDate
                                      ).startOf("day"),
                                      "days"
                                    )
                                ) + 1
                              : ""}
                          </span>
                        )}
                    </div>
                  </TableCell>
                  <TableCell>{row.onsigner}</TableCell>
                  <TableCell>
                    <span className="progress-cell">
                      <LinearProgress
                        variant="determinate"
                        color="success"
                        sx={{ height: "8px" }}
                        value={
                          row.tasks !== 0
                            ? ((row.completed + row.notApplicable) * 100) /
                              row.tasks
                            : 0
                        }
                      />
                      {row.completed + row.notApplicable + "/" + row.tasks}
                    </span>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component={"div"}
        count={(jobOrdersFromApi?.totalElements as number) || 0}
        onPageChange={(event, newPage) =>
          setJobOrderSearchParams({
            ...jobOrderSearchParams,
            page: newPage,
          })
        }
        rowsPerPageOptions={[15, 25, 50, 100]}
        page={jobOrderSearchParams?.page}
        rowsPerPage={jobOrderSearchParams?.size}
        onRowsPerPageChange={(event) =>
          setJobOrderSearchParams({
            ...jobOrderSearchParams,
            size: parseInt(event.target.value),
            page: 0,
          })
        }
      />
    </>
  );
};

export default JobOrderListComponent;
